import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Rows visible on the current page. Rendered as "Result N" on the left. */
  resultCount?: number;
}

type PageItem = number | "ellipsis";

/**
 * The page window: first and last page are always reachable, the current page
 * is always shown, and any gap collapses into a non-interactive "…". Up to
 * three pages render in full, so the window never shows a pointless ellipsis.
 */
export const getPageItems = (
  currentPage: number,
  totalPages: number
): PageItem[] => {
  if (totalPages <= 3) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 2) return [1, 2, "ellipsis", totalPages];
  if (currentPage >= totalPages - 1) {
    return [1, "ellipsis", totalPages - 1, totalPages];
  }
  return [1, "ellipsis", currentPage, "ellipsis", totalPages];
};

const cellBase =
  "min-w-[36px] h-9 px-2 inline-flex items-center justify-center rounded-md " +
  "border text-[13px] transition-colors";

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  resultCount,
}) => {
  // An empty result set still has one (empty) page, so the control never
  // renders with a zero or negative page number.
  const safeTotalPages = Math.max(1, totalPages || 1);
  const page = Math.min(Math.max(1, currentPage), safeTotalPages);

  return (
    <div className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-line">
      <div className="text-[13px] text-ink-subtle">
        {resultCount !== undefined ? `Result ${resultCount}` : null}
      </div>

      <nav aria-label="Pagination" className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Previous page"
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
          className={`${cellBase} border-line text-ink-muted hover:bg-[#F4F7F8] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {getPageItems(page, safeTotalPages).map((item, index) =>
          item === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              aria-hidden="true"
              className={`${cellBase} border-line text-ink-subtle select-none`}
            >
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              aria-label={`Page ${item}`}
              aria-current={item === page ? "page" : undefined}
              onClick={() => onPageChange(item)}
              className={`${cellBase} ${
                item === page
                  ? "border-[#5BD3E1] bg-[#EFFBFD] text-[#00424B] font-medium"
                  : "border-line text-ink-muted hover:bg-[#F4F7F8]"
              }`}
            >
              {item}
            </button>
          )
        )}

        <button
          type="button"
          aria-label="Next page"
          disabled={page === safeTotalPages}
          onClick={() => onPageChange(page + 1)}
          className={`${cellBase} border-line text-ink-muted hover:bg-[#F4F7F8] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
